import { jwtDecode } from "jwt-decode";

let refreshInFlight = null;
let scheduledRefreshTimer = null;

const isTokenExpired = (token) => {
  if (!token) return true;

  try {
    const { exp } = jwtDecode(token);
    return exp * 1000 <= Date.now();
  } catch {
    return true;
  }
};

export const requestClientToken = async () => {
  // Goes through a Netlify function so the backend client credentials stay
  // server-side (see netlify/functions/get-token.js) instead of shipping in
  // the client bundle.
  const response = await fetch("/.netlify/functions/get-token", {
    method: "POST",
    mode: "cors",
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    throw new Error("Failed to acquire access token");
  }

  const tokenResponse = await response.json();
  localStorage.setItem("token", tokenResponse.access);
  localStorage.setItem("refreshToken", tokenResponse.refresh);
  return tokenResponse.access;
};

export const scheduleTokenRefresh = () => {
  const refreshToken = localStorage.getItem("refreshToken");

  if (scheduledRefreshTimer) {
    clearTimeout(scheduledRefreshTimer);
    scheduledRefreshTimer = null;
  }

  if (refreshToken && !isTokenExpired(refreshToken)) {
    const { exp } = jwtDecode(refreshToken);
    const timeUntilExpiry = exp * 1000 - Date.now();
    const refreshTime = Math.max(timeUntilExpiry - 5 * 60 * 1000, 0);

    scheduledRefreshTimer = setTimeout(async () => {
      try {
        await getRefreshToken();
        scheduleTokenRefresh();
      } catch (error) {
        console.error("Failed to refresh token:", error);
      }
    }, refreshTime);
  }
};

export const getRefreshToken = async (retryCount = 2, delay = 500) => {
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async () => {
    const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;
    let lastError;

    for (let attempt = 0; attempt <= retryCount; attempt += 1) {
      try {
        const getAuthTokenResponse = await fetch(
          `${BACKEND_URL}token/refresh/`,
          {
            method: "POST",
            mode: "cors",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              refresh: localStorage.getItem("refreshToken"),
            }),
          },
        );

        if (!getAuthTokenResponse.ok) {
          const errorMessage = await getAuthTokenResponse.text();
          throw new Error(errorMessage || "Failed to refresh token");
        }

        const { access } = await getAuthTokenResponse.json();
        localStorage.setItem("token", access);
        return access;
      } catch (error) {
        lastError = error;
        if (attempt < retryCount) {
          await new Promise((res) => setTimeout(res, delay * (attempt + 1)));
        }
      }
    }

    throw lastError || new Error("Failed to refresh token");
  })();

  try {
    return await refreshInFlight;
  } finally {
    refreshInFlight = null;
  }
};

export const ensureAccessToken = async () => {
  const accessToken = localStorage.getItem("token");
  const refreshToken = localStorage.getItem("refreshToken");

  if (accessToken && !isTokenExpired(accessToken)) {
    return accessToken;
  }

  if (refreshToken && !isTokenExpired(refreshToken)) {
    try {
      return await getRefreshToken(1, 300);
    } catch {
      // Fall back to a client credential token request.
    }
  }

  return requestClientToken();
};

export default getRefreshToken;
